import React, { useEffect, useRef, useState } from 'react';

export default function PdfViewer({ url, height = '75vh', className = '' }) {
  const containerRef = useRef(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fallbackText, setFallbackText] = useState('');
  const [isReady, setIsReady] = useState(false);

  // Load PDF.js from CDN once
  useEffect(() => {
    if (window.pdfjsLib) {
      try {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${window.pdfjsLib.version}/pdf.worker.min.js`;
      } catch (_) {}
      setIsReady(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    script.async = true;
    script.onload = () => {
      try {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
      } catch (_) {}
      setIsReady(true);
    };
    script.onerror = () => {
      setError('Failed to load PDF renderer');
      setLoading(false);
    };
    document.body.appendChild(script);

    return () => {
      // keep script around for future openings
    };
  }, []);

  useEffect(() => {
    let destroyed = false;
    const container = containerRef.current;
    if (!container) return;

    // Clear previous render
    container.innerHTML = '';
    setLoading(true);
    setError(null);

    const render = async () => {
      try {
        if (!window.pdfjsLib) throw new Error('PDF renderer not ready');
        const loadingTask = window.pdfjsLib.getDocument({ url, withCredentials: false });
        const pdf = await loadingTask.promise;

        const totalPages = pdf.numPages || 1;
        const containerWidth = container.clientWidth || 800;

        for (let pageNumber = 1; pageNumber <= totalPages; pageNumber++) {
          const page = await pdf.getPage(pageNumber);
          const initialViewport = page.getViewport({ scale: 1 });
          const scale = Math.max(1, containerWidth / initialViewport.width);
          const viewport = page.getViewport({ scale });

          const canvas = document.createElement('canvas');
          canvas.style.width = '100%';
          canvas.style.height = 'auto';
          canvas.style.marginBottom = '16px';
          const context = canvas.getContext('2d');
          canvas.height = viewport.height;
          canvas.width = viewport.width;
          container.appendChild(canvas);

          // Render page to canvas
          // eslint-disable-next-line no-await-in-loop
          await page.render({ canvasContext: context, viewport }).promise;
        }

        if (!destroyed) setLoading(false);
      } catch (e) {
        if (!destroyed) {
          setError(e?.message || 'Failed to load PDF');
          // Fallback: try to extract text content from first few pages
          (async () => {
            try {
              if (!window.pdfjsLib) return;
              const loadingTask2 = window.pdfjsLib.getDocument({ url, disableWorker: true });
              const pdf2 = await loadingTask2.promise;
              const maxPages = Math.min(pdf2.numPages || 1, 10);
              let text = '';
              for (let i = 1; i <= maxPages; i++) {
                const page = await pdf2.getPage(i);
                const content = await page.getTextContent();
                const strings = content.items.map(item => item.str).join(' ');
                text += (i > 1 ? '\n\n' : '') + strings;
              }
              if (!destroyed) setFallbackText(text || '');
            } catch (e2) {
              // ignore, we'll just show the error state
            } finally {
              if (!destroyed) setLoading(false);
            }
          })();
        }
      }
    };

    if (isReady) {
      render();
    }

    return () => {
      destroyed = true;
    };
  }, [url, isReady]);

  return (
    <div className={className} style={{ height, overflow: 'auto' }}>
      {loading && !error && (
        <div className="flex items-center justify-center h-full text-gray-500">Loading PDF…</div>
      )}
      {error && !fallbackText && (
        <div className="p-4 text-sm text-red-600">{error}</div>
      )}
      {!!fallbackText && (
        <div className="p-4 whitespace-pre-wrap text-sm text-gray-800">{fallbackText}</div>
      )}
      <div ref={containerRef} className="w-full h-full flex flex-col items-center justify-start bg-white" />
    </div>
  );
}


